import dayjs from 'dayjs'
import { isEmpty, isNil, isNull } from 'lodash'
import { useMemo } from 'react'

import { ProductBoardItemTableRowDataFragment } from '~generated/operations'
import { ProductBoardItemsFilterInput } from '~generated/types'
import { pickValidFields } from '~lib/helpers/pickValidFields'

type ProductBoardItemsFilterKeys = keyof Omit<
  ProductBoardItemsFilterInput,
  'productLineTagIds' | 'opportunitySizeMax' | 'opportunitySizeMin'
>

export const ProductBoardItemsFilterKeys: KeysEnum<ProductBoardItemsFilterInput> = {
  query: true,
  endDateGte: true,
  endDateLte: true,
  priority: true,
  parentItemId: true,
  roadmapIds: true,
  assigneeIds: true,
  releaseIds: true,
  statusIds: true,
  launchIds: true,
  dealIds: true,
  tagIds: true,
  productLineTagIds: true,
  competitorIds: true,
  feedbackPostIds: true,
  insightIds: true,
  personaIds: true,
  accountIds: true,
  accountsFilter: true,
  opportunitySizeMax: true,
  opportunitySizeMin: true,
}

type FilterProductBoardItemsParams = {
  items: ProductBoardItemTableRowDataFragment[]
  filter: ProductBoardItemsFilterInput
}

type ProductBoardItemFilter = {
  isApplied: (item: ProductBoardItemTableRowDataFragment) => boolean
  isEmpty: () => boolean
}

type ProductBoardItemFilterInstance = Record<ProductBoardItemsFilterKeys, ProductBoardItemFilter>

function isMatchesArrayFilter(value: string, filter: string[]) {
  if (filter === null) {
    return !value
  }

  if (filter?.length > 0) {
    return filter.includes(value)
  }

  return true
}

function isMatchesNestedArrayFilter<
  T extends string | Record<string, any>,
  K = T extends Record<string, any> ? keyof T : never
>(items: T[] | T[], filter: string[], key: K = null) {
  if (filter === null) {
    return !(items && items.length > 0)
  }

  if (filter?.length > 0) {
    return items?.some((item) => {
      const value = typeof item === 'string' ? item : item[key as keyof T]
      return filter.includes(value as string)
    })
  }

  return true
}

function isGroupFilterEmpty(filter: unknown) {
  return !isNull(filter) && isEmpty(filter)
}

function getApplicableFilters(filter: ProductBoardItemsFilterInput): ProductBoardItemFilter[] {
  if (!filter) return []
  const queryRegExp = filter.query ? new RegExp(filter.query, 'i') : null

  const filterInstance: ProductBoardItemFilterInstance = {
    query: {
      isApplied: (item) => {
        if (!queryRegExp) return true
        return queryRegExp.test(item.title)
      },
      isEmpty: () => {
        return !filter.query
      },
    },

    endDateLte: {
      isApplied: (item) => {
        if (!item.endDate) return false
        if (!filter.endDateGte) return true
        return dayjs(item.endDate).valueOf() >= dayjs(filter.endDateGte).valueOf()
      },
      isEmpty: () => {
        return !filter.endDateGte
      },
    },

    endDateGte: {
      isApplied: (item) => {
        if (!item.endDate) return false
        if (!filter.endDateLte) return true
        return dayjs(item.endDate).valueOf() <= dayjs(filter.endDateLte).valueOf()
      },
      isEmpty: () => {
        return !filter.endDateGte
      },
    },

    priority: {
      isApplied: (item) => {
        return isMatchesArrayFilter(item.priority, filter.priority)
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.priority)
      },
    },

    parentItemId: {
      isApplied: (item) => {
        if (filter.parentItemId === undefined) return true
        return filter.parentItemId === item.parentItemId
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.parentItemId)
      },
    },

    roadmapIds: {
      isApplied: (item) => {
        return isMatchesArrayFilter(item.roadmapId, filter.roadmapIds)
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.roadmapIds)
      },
    },

    assigneeIds: {
      isApplied: (item) => {
        return isMatchesArrayFilter(item.assignee?.id, filter.assigneeIds)
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.assigneeIds)
      },
    },

    releaseIds: {
      isApplied: (item) => {
        return isMatchesArrayFilter(item.release?.id, filter.releaseIds)
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.releaseIds)
      },
    },

    statusIds: {
      isApplied: (item) => {
        return isMatchesArrayFilter(item.status?.id, filter.statusIds)
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.statusIds)
      },
    },

    launchIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.launches, filter.launchIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.launchIds)
      },
    },

    tagIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.tags, filter.tagIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.tagIds)
      },
    },

    dealIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.deals, filter.dealIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.dealIds)
      },
    },

    competitorIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.competitors, filter.competitorIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.competitorIds)
      },
    },
    feedbackPostIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.feedbackPosts, filter.feedbackPostIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.feedbackPostIds)
      },
    },
    insightIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.insights, filter.insightIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.insightIds)
      },
    },
    personaIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.personas, filter.personaIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.personaIds)
      },
    },
    accountIds: {
      isApplied: (item) => {
        return isMatchesNestedArrayFilter(item.accounts, filter.accountIds, 'id')
      },
      isEmpty: () => {
        return isGroupFilterEmpty(filter.accountIds)
      },
    },
    // TODO: Support for flat filters so nesting mess can be avoided
    accountsFilter: {
      isApplied: (item) => {
        const isDateFilterApplied = (dateKey) => {
          return item.accounts?.some((account) => {
            if (!account[`${dateKey}Gte`]) return false
            if (!filter.accountsFilter?.[`${dateKey}Lte`]) return true
            return (
              dayjs(account[`${dateKey}Gte`]).valueOf() <= dayjs(filter.accountsFilter?.[`${dateKey}Lte`]).valueOf()
            )
          })
        }

        const isNumberFilterApplied = (numberKey) => {
          return item.accounts?.some((account) => {
            if (!account[`${numberKey}Gte`]) return false
            if (!filter.accountsFilter?.[`${numberKey}Lte`]) return true
            return Number(account[`${numberKey}Gte`]) <= Number(filter.accountsFilter?.[`${numberKey}Lte`])
          })
        }

        return (
          isMatchesNestedArrayFilter(item.accounts, filter.accountsFilter?.type, 'type') ||
          isMatchesNestedArrayFilter(item.accounts, filter.accountsFilter?.industry, 'industry') ||
          isDateFilterApplied('createdAt') ||
          isDateFilterApplied('updatedAt') ||
          isNumberFilterApplied('accountValue') ||
          isNumberFilterApplied('annualRevenue') ||
          isNumberFilterApplied('employees')
        )
      },
      isEmpty: () => {
        return (
          isEmpty(filter.accountsFilter) ||
          (isGroupFilterEmpty(filter.accountsFilter?.type) &&
            isGroupFilterEmpty(filter.accountsFilter?.industry) &&
            isEmpty(
              pickValidFields(filter.accountsFilter, [
                {
                  keys: [
                    'createdAtGte',
                    'createdAtLte',
                    'updatedAtGte',
                    'updatedAtLte',
                    'accountValueGte',
                    'accountValueLte',
                    'annualRevenueGte',
                    'annualRevenueLte',
                    'employeesGte',
                    'employeesLte',
                  ],
                  validator: (v) => !isNil(v),
                },
              ])
            ))
        )
      },
    },
  }

  return Object.keys(filter).reduce<ProductBoardItemFilter[]>((acc, key) => {
    if (filterInstance[key]) acc.push(filterInstance[key])
    return acc
  }, [])
}

export function filterProductBoardItems({ items, filter }: FilterProductBoardItemsParams) {
  const applicableFilters = getApplicableFilters(filter)

  if (applicableFilters.every((filter) => filter.isEmpty())) {
    return items
  }

  return items.filter((item) => {
    return applicableFilters.every((filter) => filter.isApplied(item))
  })
}

function useFilterProductBoardItems({ items, filter }: FilterProductBoardItemsParams) {
  return useMemo(() => filterProductBoardItems({ items, filter }), [filter, items])
}

export default useFilterProductBoardItems
